import React from "react";
import { usePut } from "../../../../api/useCall";
import useSortedData from "../../../../lib/hooks/useSortedData";
import t from "../../../../i18n";
import { indexBy } from "diagnostics-lib/iterable";
import Button from "@ui/Button";
import ModalForm from "@ui/Modal/Form";
import Select, { type SelectOption } from "@ui/Select";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { useCanUserDoOnProject } from "../../../canUserDo";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { AddCircleLine, CloseLine } from "@ui/IconV2/react";
import { css } from "@linaria/core";
import { type Quantity } from "../../../../api/useQuantities";
import { Tag } from "@ui/Tag";
import TooltipV2 from "@ui/TooltipV2";
import type { QoiRole } from "diagnostics-domain/aggregationPeriod";
import InputV2 from "@ui/Input/InputV2";
import Ellipsis from "@ui/Ellipsis";

const columns = ["name", "role", "referenceValue", "qoiOrder", "deleteIcon"] as const;
type Column = (typeof columns)[number];
export const defaultQoiRolesOptions = [
  {
    value: "POWER",
    label: t("project.settings.qoi.role.power"),
  },
];
const computeRowKey = (qoi: Qoi) => `qoi-${qoi.qoiOrder}-${qoi.id}`;
const regexNumber = /^[+-]?(?:\d+(?:[.,]\d+)?|[.,]\d+)(?:[eE][+-]?\d+)?$/;
const MAX_QOI_COUNT = 5;

interface Qoi {
  id: string;
  qoiOrder: number;
  name: string;
  selectedQoiRole: QoiRole | null;
  qoiReferenceValue: string | null;
  qoiRolesOptions: SelectOption[];
}
interface P {
  closeModal: () => void;
  qois: {
    id: string;
    selectedQoiRole: QoiRole | null;
    qoiReferenceValue: number | null;
    qoiOrder: number;
    name: string;
  }[];
  quantities: Quantity[];
  projectId: string;
}

const ManageQoiModalForm = ({ quantities, projectId, closeModal, qois }: P) => {
  const canUserEdit = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const updateQois = usePut("/projects/:projectId(uuid)/quantities/qois");
  const sortedQois = useSortedData(qois, qoi => qoi.qoiOrder, "asc");
  const finalQois = sortedQois.map(qoi => ({
    ...qoi,
    qoiReferenceValue: qoi.qoiReferenceValue?.toString() ?? "",
    selectedQoiRole: qoi.selectedQoiRole,
    qoiRolesOptions: defaultQoiRolesOptions.filter(
      option => !sortedQois.some(q => option.value === q.selectedQoiRole && q.id !== qoi.id),
    ),
    qoiReferenceValueError: "",
  }));
  const [qoisData, setQoisData] = React.useState(finalQois);
  const defaultQuantitiesOptions = quantities
    .map(quantity => {
      const extra = <>{quantity.unit && <Tag size="s">{quantity.unit}</Tag>}</>;
      return {
        value: quantity.id,
        label: `${quantity.name}`,
        extra,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
  const quantityById = indexBy(defaultQuantitiesOptions, "value");

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<Qoi> } = {
      name: {
        label: t("project.settings.qoi.table.column.quantity"),
        verticalAlignmentCenter: false,
        render: qoi => {
          const finalQuantitiesOptions = defaultQuantitiesOptions.filter(
            quantity => quantity.value === qoi.id || !qoisData.some(q => q.id === quantity.value),
          );

          return (
            <Select
              filtered
              className={CellSelect}
              onChange={e => {
                const quantityId = e.target.value;
                const quantity = quantityById.get(quantityId);

                if (!quantity) {
                  return;
                }
                setQoisData(previousQois =>
                  previousQois.map(q =>
                    q.qoiOrder === qoi.qoiOrder
                      ? { ...q, id: quantityId, name: quantity.label }
                      : q,
                  ),
                );
              }}
              value={qoi.id}
              options={finalQuantitiesOptions}
              prompt={t("project.settings.qoi.prompt")}
              clearable={false}
            />
          );
        },
      },
      role: {
        label: t("project.settings.qoi.table.column.role"),
        verticalAlignmentCenter: false,
        render: qoi => (
          <Select
            className={CellSelect}
            onChange={e => {
              const value = e.target.value;

              setQoisData(previousQois =>
                previousQois.map(q =>
                  q.id === qoi.id
                    ? { ...q, selectedQoiRole: value === "" ? null : (value as QoiRole) }
                    : {
                        ...q,
                        qoiRolesOptions: defaultQoiRolesOptions.filter(el => el.value !== value),
                      },
                ),
              );
            }}
            value={qoi.selectedQoiRole || ""}
            options={qoi.qoiRolesOptions}
            disabled={qoi.qoiRolesOptions.length === 0}
            prompt={t("project.settings.qoi.prompt")}
          />
        ),
        width: "240px",
      },
      referenceValue: {
        label: t("project.settings.qoi.table.column.referenceValue"),
        verticalAlignmentCenter: false,
        render: qoi => {
          const qoisData_ = qoisData.find(q => q.id === qoi.id);
          const qoiReferenceValueError = qoisData_?.qoiReferenceValueError;

          return (
            <InputV2
              type="text"
              placeholder={t("common.value")}
              onChange={e => {
                setQoisData(previousQois =>
                  previousQois.map(q => {
                    if (q.id !== qoi.id) return q;

                    if (regexNumber.test(e.target.value) || e.target.value === "") {
                      return {
                        ...q,
                        qoiReferenceValue: e.target.value ?? "",
                        qoiReferenceValueError: "",
                      };
                    }

                    return {
                      ...q,
                      qoiReferenceValueError: t(
                        "project.settings.qoi.table.column.referenceValue.error",
                      ),
                    };
                  }),
                );
              }}
              value={qoi.qoiReferenceValue || ""}
              error={
                qoiReferenceValueError ? (
                  <Ellipsis maxNumberOfLines={1}>{qoiReferenceValueError}</Ellipsis>
                ) : undefined
              }
            />
          );
        },
        width: "145px",
      },
      qoiOrder: {
        label: t("project.settings.qoi.table.column.order"),
        render: qoi => <div className={qoiOrderCss}>{qoi.qoiOrder}</div>,
        width: "125px",
      },
      deleteIcon: {
        label: null,
        width: "40px",
        render: qoi =>
          canUserEdit ? (
            <>
              <TooltipV2 overlay={t("modalForm.button.name.remove")} placement="top">
                <ButtonIconV2
                  onClick={() => {
                    setQoisData(previousQois => {
                      const newQoisData = previousQois.filter(q => q.qoiOrder !== qoi.qoiOrder);

                      return newQoisData.map((qoi, index) => ({
                        ...qoi,
                        qoiRolesOptions: defaultQoiRolesOptions.filter(
                          option =>
                            !newQoisData.some(
                              q => option.value === q.selectedQoiRole && q.id !== qoi.id,
                            ),
                        ),
                        qoiOrder: index + 1,
                      }));
                    });
                  }}
                >
                  <CloseLine />
                </ButtonIconV2>
              </TooltipV2>
            </>
          ) : (
            <></>
          ),
      },
    };

    return _;
  }, [canUserEdit, qoisData, quantityById, defaultQuantitiesOptions]);
  const maxQoiOrder = React.useMemo(
    () => qoisData.reduce((acc, qoi) => Math.max(acc, qoi.qoiOrder), 0),
    [qoisData],
  );
  const isDirty = React.useMemo(
    () =>
      JSON.stringify(finalQois.map(qoi => qoi.name)) !==
        JSON.stringify(qoisData.map(qoi => qoi.name)) ||
      JSON.stringify(finalQois.map(qoi => qoi.selectedQoiRole)) !==
        JSON.stringify(qoisData.map(qoi => qoi.selectedQoiRole)) ||
      JSON.stringify(finalQois.map(qoi => qoi.qoiReferenceValue)) !==
        JSON.stringify(qoisData.map(qoi => qoi.qoiReferenceValue)),
    [qoisData, finalQois],
  );

  const valid =
    isDirty &&
    qoisData.every(qoi => qoi.id !== "") &&
    qoisData.every(qoi => qoi.qoiReferenceValue === "" || regexNumber.test(qoi.qoiReferenceValue));

  const handleSubmit = React.useCallback(async () => {
    await updateQois({
      routeParams: { projectId },
      body: qoisData.map(({ id, selectedQoiRole, qoiReferenceValue }) => ({
        id,
        qoiRole: selectedQoiRole,
        qoiReferenceValue: qoiReferenceValue ? parseFloat(qoiReferenceValue) : null,
      })),
    });

    return true;
  }, [projectId, qoisData, updateQois]);

  const handleShowNewQoi = React.useCallback(() => {
    setQoisData(previousQois => [
      ...previousQois,
      {
        id: "",
        qoiOrder: maxQoiOrder + 1,
        name: "",
        selectedQoiRole: null,
        qoiReferenceValue: "",
        qoiReferenceValueError: "",
        qoiRolesOptions: defaultQoiRolesOptions.filter(
          option => !previousQois.some(q => option.value === q.selectedQoiRole),
        ),
      },
    ]);
  }, [maxQoiOrder]);

  const disableAddButton = () => {
    if (qoisData.find(qoi => qoi.id === "")) {
      return true;
    }
    if (qoisData.length >= MAX_QOI_COUNT) {
      return true;
    }
    return false;
  };

  return (
    <ModalForm
      onSubmit={valid ? handleSubmit : false}
      onRequestClose={closeModal}
      okButtonName={t("modalForm.button.name.save")}
    >
      {qoisData.length > 0 ? (
        <Table datas={qoisData} metadata={metadata} computeRowKey={computeRowKey} />
      ) : (
        <>{t("project.settings.qoi.no.data")}</>
      )}

      {canUserEdit && (
        <>
          <Button
            className={styledButton}
            variant="link"
            onClick={handleShowNewQoi}
            leftIcon={<AddCircleLine />}
            disabled={disableAddButton()}
          >
            {t("project.settings.qoi.add.new")}
          </Button>
        </>
      )}
    </ModalForm>
  );
};

export default ManageQoiModalForm;

//#region CSS
const qoiOrderCss = css`
  text-align: center;
  width: 100%;
`;
const styledButton = css`
  align-self: flex-start;
`;
const CellSelect = css`
  width: 100%;
`;
//#endregion CSS
